import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useAuth } from "@/auth/AuthProvider";
import { DatePicker } from "@/components/DatePicker";
import { Choice, FieldError, Req, SelectField } from "@/components/fields";
import type { PDate } from "@/plan-logic/lib/dates";
import { PriorityList, PriorityPicker } from "@/priorities/PriorityEditor";
import { newPerson, PROVINCES, syncEducation } from "@/plan-logic/questionnaire/draft";
import { draftAdults, retirementAgeOptions } from "@/questionnaire/retirementAge";
import { HowMap, ONB_HOWMAP } from "@/site/widgets/HowMap";
import { STEP_FIELDS } from "../schema";
import { Screen, useOnbNav } from "../Screen";
import { removeAt, updateAt, useDraft } from "../useDraft";
import { AccountFields, useAccountFields } from "./AccountFields";

const CARD_SUB = { color: "var(--ink-3)", fontSize: 13.5, margin: "4px 0 16px" } as const;

/** The welcome route's six checkpoints (v46), each an icon (SVG paths), a title and a line. */
const WELCOME_STEPS: { icon: string; b: string; p?: string }[] = [
  {
    icon: '<path d="M12 3v4m0 10v4M3 12h4m10 0h4"/><circle cx="12" cy="12" r="4.4"/>',
    b: "Tell us your priorities",
    p: "Abi optimizes for them.",
  },
  {
    icon: '<path d="M9.5 14.5 14.5 9.5"/><path d="M11 7.4 12.9 5.5a3.6 3.6 0 0 1 5.1 5.1l-1.9 1.9"/><path d="M13 16.6 11.1 18.5a3.6 3.6 0 0 1-5.1-5.1l1.9-1.9"/>',
    // v46's "Abi does the heavy lifting of compiling your financial history." is left out until bank
    // and investment connections exist (docs/design/ux-issues.md)
    b: "Connect your accounts",
  },
  {
    icon: '<path d="M4 20h16"/><path d="M6 16V9m6 7V5m6 11v-4"/>',
    b: "Fill in anything missing",
    p: "To make your plan as sharp as possible.",
  },
  {
    icon: '<path d="M3 17l5-6 4 4 5-7 4 5"/><circle cx="8" cy="11" r="1.4" fill="currentColor" stroke="none"/>',
    b: "See Abi’s impact",
    p: "The handful of numbers that move the most, before against after.",
  },
  {
    icon: '<rect x="3" y="4" width="18" height="15" rx="2.4"/><path d="M3 9h18M8 13h4m-4 3h7"/>',
    b: "Access your dashboard",
    p: "Built to give you a view of your finances you have never had.",
  },
  {
    icon: '<path d="M12 21s-7-4.6-7-10a7 7 0 0 1 14 0c0 5.4-7 10-7 10Z"/><circle cx="12" cy="11" r="2.5"/>',
    b: "Watch it evolve with you",
    p: "With you for every financial decision, big or small.",
  },
];

/** Welcome (v46): the six-checkpoint route the How it works page draws, in onboarding's palette. */
export function WelcomeStep() {
  return (
    <Screen eyebrow="Welcome" h1="onboarding.welcome.h1" nextLabel="Get started">
      <HowMap
        classes={ONB_HOWMAP}
        id="omap"
        svgId="opath"
        animate="mount"
        steps={WELCOME_STEPS.map((s) => (
          <>
            <span className="osi">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                dangerouslySetInnerHTML={{ __html: s.icon }}
              />
            </span>
            <b>{s.b}</b>
            {s.p && <p>{s.p}</p>}
          </>
        ))}
      />
    </Screen>
  );
}

/**
 * The education Abi adds for each child under 18 (v39), kept in step with the children: added,
 * renamed, redated, or dropped as the household step changes them (`syncEducation`).
 */
function useEducationSync(today: PDate) {
  const { draft, patch } = useDraft();
  const kids = draft.hasKids ? draft.kids : [];
  const key = JSON.stringify(kids.map((k) => [k.uuid, k.givenName, k.dob?.year]));
  useEffect(() => {
    const next = syncEducation(draft.priorities, kids, today, draft.dismissedEducation);
    if (next !== draft.priorities) patch("priorities", next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, today]);
}

/**
 * Step 2 (v47's order, Ian 2026-11-01): what matters, "Retiring comfortably" included, with the
 * education Abi added for the children named on the household step.
 */
export function PrioritiesStep({ eyebrow, today }: { eyebrow: string; today: PDate }) {
  const { draft, patch, err } = useDraft();
  useEducationSync(today);
  const hasDebt = draft.hasDebts || draft.accounts.some((a) => a.kind === "credit");
  return (
    <Screen eyebrow={eyebrow} h1="onboarding.priorities.h1" sub="onboarding.priorities.sub">
      <div className="note-soft" style={{ marginBottom: 18 }}>
        This is the one thing the plan cannot be built without. Nothing here is final: add, change
        and remove priorities any time from your dashboard, and the plan rebuilds around the new
        ones.
      </div>
      <div className="card pad">
        <h2>Common priorities</h2>
        <p style={CARD_SUB}>Tap to add. A rough number is fine, we&rsquo;ll sharpen it later.</p>
        <PriorityPicker
          priorities={draft.priorities}
          onAdd={(p) => patch("priorities", [...draft.priorities, p])}
        />
      </div>
      <div className="card pad mt">
        <h2>Your priorities</h2>
        <p style={{ ...CARD_SUB, margin: "4px 0 14px" }}>
          Score each one from 1 to 10. Ties are fine &mdash; if three things matter equally, give
          all three a 10.
        </p>
        <PriorityList
          priorities={draft.priorities}
          onChange={(next) => patch("priorities", next)}
          retirementImportance={draft.retirementImportance}
          onRetirementImportance={(v) => patch("retirementImportance", v)}
          retirement={draft.retirement}
          onRetirement={(r) =>
            patch("retirement", { pct: r.pct ?? draft.retirement.pct, age: r.age })
          }
          ageOptions={retirementAgeOptions(draftAdults(draft), today)}
          onRemoveEducation={(kid) =>
            patch("dismissedEducation", [...draft.dismissedEducation, kid])
          }
          hasDebt={hasDebt}
          kids={draft.hasKids ? draft.kids : []}
          err={err}
        />
      </div>
    </Screen>
  );
}

const PARTNER_CHOICES = [
  {
    v: "married",
    ic: "💍",
    t: "Married",
    d: "You can split some retirement income and share a few credits.",
  },
  {
    v: "commonlaw",
    ic: "🏠",
    t: "Living common-law",
    d: "Twelve months together, or sooner if you have a child together. The CRA treats this almost the same as married.",
  },
  { v: "single", ic: "👤", t: "Just me", d: "One income, one set of accounts." },
] as const;

/**
 * Step 1 (v47's order): you, your partner, your children. Signed out, "About you" also asks for the
 * email and a password (where v47 asks for the email), and Continue creates the account first.
 */
export function HouseholdStep({ eyebrow, today }: { eyebrow: string; today: PDate }) {
  const { draft, patch, err } = useDraft();
  const { user } = useAuth();
  const nav = useOnbNav();
  const { trigger } = useFormContext();
  const account = useAccountFields();
  const [joining, setJoining] = useState(false);
  // Signed up: move on once the session is there (the later steps send anyone without one back here).
  const [advance, setAdvance] = useState(false);
  useEffect(() => {
    if (!advance || !user) return;
    setAdvance(false);
    void nav.next();
  }, [advance, user, nav]);
  useEducationSync(today);

  const next = async () => {
    // the household's own fields first, so nobody signs up and is then sent back here
    if (!(await trigger(STEP_FIELDS.household as never))) {
      await account.form.trigger();
      return;
    }
    setJoining(true);
    const email = await account.submit();
    setJoining(false);
    if (!email) return;
    patch("email", email);
    setAdvance(true);
  };

  return (
    <Screen
      eyebrow={eyebrow}
      h1="onboarding.household.h1"
      sub="onboarding.household.sub"
      maxWidth={620}
      onNext={user ? undefined : next}
      nextLabel={joining ? "One moment…" : "Continue"}
      nextDisabled={joining}
    >
      <div className="card pad">
        <h2>About you</h2>
        <p style={CARD_SUB}>The basics every date in your plan is measured from.</p>
        <div className="two">
          <div className="field">
            <label htmlFor="fn">
              First name <Req />
            </label>
            <input
              id="fn"
              type="text"
              value={draft.self.givenName}
              onChange={(e) => patch("self", { ...draft.self, givenName: e.target.value })}
              data-testid="self-given-name"
              autoComplete="given-name"
            />
            <FieldError error={err("self.givenName")} />
          </div>
          <div className="field">
            <label htmlFor="ln">Last name</label>
            <input
              id="ln"
              type="text"
              value={draft.self.familyName}
              onChange={(e) => patch("self", { ...draft.self, familyName: e.target.value })}
              autoComplete="family-name"
            />
          </div>
        </div>
        <div className="field">
          <label>
            Date of birth <Req />
          </label>
          <DatePicker
            value={draft.self.dob}
            onChange={(d) => patch("self", { ...draft.self, dob: d })}
            mode="past"
            day
            anchor={new Date().getFullYear() - 45}
            placeholder="Pick a birthday"
            label="Your date of birth"
            testId="self-dob"
          />
          <p className="hint">
            Every date in your plan is measured from this: when you can draw on an RRSP without
            penalty, when CPP and OAS start, how long the money has to last.
          </p>
          <FieldError error={err("self.dob")} />
        </div>
        <SelectField
          label="Where you live"
          value={draft.province}
          options={PROVINCES}
          onChange={(v) => patch("province", v)}
          testId="province"
        />
        <p className="hint" style={{ marginTop: -8 }}>
          Income tax is set provincially as well as federally.
        </p>
        {user ? (
          <p className="hint" data-testid="onb-signed-in">
            You&rsquo;re signed in as <b>{user.email}</b>. Your answers will be saved to this
            account.
          </p>
        ) : (
          <AccountFields account={account} />
        )}
      </div>

      <div className="card pad">
        <h2>Your partner</h2>
        <p style={CARD_SUB}>
          In Canada this changes your tax, your benefits and what you can move between each other.
        </p>
        <div className="choices">
          {PARTNER_CHOICES.map((c) => (
            <Choice
              key={c.v}
              selected={draft.partnerStatus === c.v}
              onClick={() => patch("partnerStatus", c.v)}
              icon={c.ic}
              title={c.t}
              testId={`partner-${c.v}`}
            >
              {c.d}
            </Choice>
          ))}
        </div>
        {draft.partnerStatus !== "single" && (
          <div style={{ marginTop: 16 }}>
            <div className="rowbox">
              <div className="rowhead">
                <b>Your partner</b>
              </div>
              <div className="two">
                <div className="field" style={{ margin: 0 }}>
                  <label htmlFor="pfn">
                    First name <Req />
                  </label>
                  <input
                    id="pfn"
                    type="text"
                    value={draft.partner.givenName}
                    onChange={(e) =>
                      patch("partner", { ...draft.partner, givenName: e.target.value })
                    }
                    data-testid="partner-given-name"
                  />
                  <FieldError error={err("partner.givenName")} />
                </div>
                <div className="field" style={{ margin: 0 }}>
                  <label>
                    Date of birth <Req />
                  </label>
                  <DatePicker
                    value={draft.partner.dob}
                    onChange={(d) => patch("partner", { ...draft.partner, dob: d })}
                    mode="past"
                    day
                    anchor={new Date().getFullYear() - 45}
                    placeholder="Pick a birthday"
                    label="Your partner's date of birth"
                  />
                  <FieldError error={err("partner.dob")} />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="card pad mt">
        <h2>Do you have children?</h2>
        <p style={CARD_SUB}>
          Birthdays, not ages. An RESP has to be open long enough to collect the government grant,
          and the grant stops the year a child turns 17, so the date is the whole thing.
        </p>
        <div className="choices">
          <Choice
            selected={draft.hasKids}
            onClick={() => {
              patch("hasKids", true);
              if (!draft.kids.length) patch("kids", [newPerson()]);
            }}
            icon="👶"
            title="Yes"
            testId="kids-yes"
          >
            Tell us their names and birthdays.
          </Choice>
          <Choice
            selected={!draft.hasKids}
            onClick={() => patch("hasKids", false)}
            icon="—"
            title="No"
            testId="kids-no"
          >
            Not now, or not ever. You can add them whenever that changes.
          </Choice>
        </div>
        {draft.hasKids && (
          <div style={{ marginTop: 16 }}>
            {draft.kids.map((k, i) => (
              <div className="rowbox" key={k.uuid}>
                <div className="rowhead">
                  <b>Child {i + 1}</b>
                  {i > 0 && (
                    <button
                      type="button"
                      className="rm"
                      onClick={() => patch("kids", removeAt(draft.kids, i))}
                    >
                      Remove
                    </button>
                  )}
                </div>
                <div className="two">
                  <div className="field" style={{ margin: 0 }}>
                    <label htmlFor={`kid-${i}`}>
                      First name <Req />
                    </label>
                    <input
                      id={`kid-${i}`}
                      type="text"
                      value={k.givenName}
                      onChange={(e) =>
                        patch(
                          "kids",
                          updateAt(draft.kids, i, (x) => ({ ...x, givenName: e.target.value })),
                        )
                      }
                    />
                    <FieldError error={err(`kids.${i}.givenName`)} />
                  </div>
                  <div className="field" style={{ margin: 0 }}>
                    <label>
                      Date of birth <Req />
                    </label>
                    <DatePicker
                      value={k.dob}
                      onChange={(d) =>
                        patch(
                          "kids",
                          updateAt(draft.kids, i, (x) => ({ ...x, dob: d })),
                        )
                      }
                      mode="past"
                      day
                      anchor={new Date().getFullYear() - 11}
                      placeholder="Pick a birthday"
                      label={`Child ${i + 1}'s date of birth`}
                    />
                    <FieldError error={err(`kids.${i}.dob`)} />
                  </div>
                </div>
              </div>
            ))}
            <FieldError error={err("kids")} />
            <button
              type="button"
              className="mini"
              onClick={() => patch("kids", [...draft.kids, newPerson()])}
            >
              + Add another child
            </button>
          </div>
        )}
      </div>
    </Screen>
  );
}
